import React, { type ComponentType, type PropsWithChildren, useEffect, useMemo } from 'react';

import { ErrorBoundary, type FallbackProps } from 'react-error-boundary';

import { type ReliabilityAttributes, studioReliabilityTracker } from './studio-reliability-tracker';

export type ReliabilityStartProps = {
	attributes?: ReliabilityAttributes;
	task: string;
	taskId?: string;
	touchpointId?: string;
};

export const ReliabilityStart = ({
	task,
	taskId,
	touchpointId,
	attributes,
}: ReliabilityStartProps): null => {
	useMemo(
		() => {
			studioReliabilityTracker.start({ task, taskId, touchpointId, attributes });
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[task, taskId, touchpointId],
	);
	return null;
};

export type ReliabilitySucceedProps = {
	attributes?: ReliabilityAttributes;
	task: string;
	taskId?: string;
};

export const ReliabilitySucceed = ({ task, taskId, attributes }: ReliabilitySucceedProps): null => {
	useEffect(
		() => {
			studioReliabilityTracker.succeed({ task, taskId, attributes });
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[task, taskId],
	);
	return null;
};

export type ReliabilityFailureProps = {
	attributes?: ReliabilityAttributes;
	error: Error;
	task: string;
	taskId?: string;
	traceId?: string;
};

export const ReliabilityFailure = ({
	task,
	taskId,
	error,
	traceId,
	attributes,
}: ReliabilityFailureProps): null => {
	useEffect(
		() => {
			studioReliabilityTracker.fail({ task, taskId, error, traceId, attributes });
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[task, taskId, error],
	);
	return null;
};

export type ReliabilityErrorBoundaryProps = {
	fallback: ComponentType<FallbackProps>;
	task: string;
	taskId?: string;
};

export const ReliabilityErrorBoundary = ({
	task,
	taskId,
	fallback,
	children,
}: PropsWithChildren<ReliabilityErrorBoundaryProps>): React.JSX.Element => (
	<ErrorBoundary
		FallbackComponent={fallback}
		onError={(error) => {
			studioReliabilityTracker.fail({ task, taskId, error });
		}}
	>
		{children}
	</ErrorBoundary>
);
